import api from './api';
import { Setup, SetupNode, SetupNodeSession } from '../types';

class SetupService {
  async getAll(): Promise<Setup[]> {
    const response = await api.get('/setups/');
    return response.data.results || response.data;
  }

  async getSetup(id: number): Promise<Setup> {
    const response = await api.get(`/setups/${id}/`);
    return response.data;
  }

  async create(data: Partial<Setup>): Promise<Setup> {
    const response = await api.post('/setups/', data);
    return response.data;
  }

  async update(id: number, data: Partial<Setup>): Promise<Setup> {
    const response = await api.patch(`/setups/${id}/`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/setups/${id}/`);
  }

  async copy(id: number): Promise<Setup> {
    const response = await api.post(`/setups/${id}/copy/`);
    return response.data;
  }

  async setActive(id: number): Promise<void> {
    await api.post(`/setups/${id}/set_active/`);
  }

  async getActive(): Promise<Setup | null> {
    const response = await api.get('/setups/active/');
    return response.data;
  }

  async getAvailable(): Promise<Setup[]> {
    const response = await api.get('/setups/available/');
    return response.data.results || response.data;
  }

  // Node management
  async getNodes(setupId: number): Promise<SetupNode[]> {
    const response = await api.get(`/setups/${setupId}/nodes/`);
    return response.data;
  }

  async createNode(setupId: number, data: Partial<SetupNode>): Promise<SetupNode> {
    const response = await api.post(`/setups/${setupId}/nodes/create/`, data);
    return response.data;
  }

  async updateNode(setupId: number, nodeId: number, data: Partial<SetupNode>): Promise<SetupNode> {
    const response = await api.put(`/setups/${setupId}/nodes/${nodeId}/update/`, data);
    return response.data;
  }

  async deleteNode(setupId: number, nodeId: number): Promise<void> {
    await api.delete(`/setups/${setupId}/nodes/${nodeId}/delete/`);
  }

  async reorderNodes(setupId: number, nodeOrders: { id: number; order: number }[]): Promise<void> {
    await api.post(`/setups/${setupId}/nodes/reorder/`, { node_orders: nodeOrders });
  }

  // Session management for leaf nodes
  async addSessionToNode(setupId: number, nodeId: number, sessionId: number, order?: number): Promise<SetupNodeSession> {
    const response = await api.post(`/setups/${setupId}/nodes/${nodeId}/sessions/add/`, {
      session_id: sessionId,
      order: order || 0
    });
    return response.data;
  }

  async removeSessionFromNode(setupId: number, nodeId: number, sessionId: number): Promise<void> {
    await api.delete(`/setups/${setupId}/nodes/${nodeId}/sessions/${sessionId}/remove/`);
  }
}

const setupService = new SetupService();
export default setupService;
